import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Alert, IconButton,
} from '@mui/material';

// Actions
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Side deck icons
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import GraphView from '../components/GraphView';
import ScoredRedFlagsList from '../components/ScoredRedFlagsList';
import RedFlagsCard from '../components/RedFlagsCard';
import AlertTimeline from '../components/AlertTimeline';
import IndependentVerificationPanel from '../components/IndependentVerificationPanel';
import OsintPanel from '../components/OsintPanel';
import DepositTraceCard from '../components/DepositTraceCard';
import AgentChatPanel from '../components/AgentChatPanel';
import ReportEditor from '../components/ReportEditor';
import DecisionPanel from '../components/DecisionPanel';
import AgentAnalysisModal from '../components/AgentAnalysisModal';
import ReportDialog from '../components/ReportDialog';

import { colors, typography, shadows, gradient } from '../theme/designTheme';

import { useCase } from '../hooks/useCase';
import { useCases } from '../hooks/useCases';
import { useCaseGraph } from '../hooks/useCaseGraph';
import { useCaseRedFlags } from '../hooks/useCaseRedFlags';
import { useCaseVerifications } from '../hooks/useCaseVerifications';
import { useScoredRedFlags } from '../hooks/useScoredRedFlags';
import { useOsint, useDepositTrace, useCaseNetwork } from '../hooks/useNetwork';
import { useAgenticAnalysis } from '../hooks/useAnalysis';
import { useDraftReport } from '../hooks/useDraftReport';

// ── Small primitives ──────────────────────────────────────────────────

const Mono = ({ children, sx }) => (
  <Box component="span" sx={{
    fontFamily: typography.fontFamilyMono, fontFeatureSettings: '"tnum" 1', ...sx,
  }}>
    {children}
  </Box>
);

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtMoney = (n) => n == null ? null : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// Split a section body into bullet points. Honours pre-existing bullets
// (`- ` / `• ` / `* ` at line starts); otherwise splits on sentence boundaries.
const splitToBullets = (text) => {
  if (!text) return [];
  if (/\n\s*[-•*]\s/.test(text)) {
    return text.split(/\n/).map((s) => s.replace(/^\s*[-•*]\s*/, '').trim()).filter(Boolean);
  }
  return text
    .split(/(?<=[.;])\s+(?=[A-Z"'“])/)
    .map((s) => s.trim())
    .filter((s) => s.length > 10);
};

const NetworkSignalsCard = ({ section, reportName }) => {
  const bullets = useMemo(() => splitToBullets(section?.content), [section?.content]);
  if (!section || bullets.length === 0) return null;
  return (
    <Box sx={{
      position: 'fixed', top: 16, right: 16, bottom: 16, width: 344, zIndex: 1300,
      bgcolor: colors.surface, borderRadius: '16px', boxShadow: shadows.elevated,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Box sx={{ p: '20px 22px 12px', borderBottom: `1px solid ${colors.canvas}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <HubOutlinedIcon sx={{ fontSize: 16, color: colors.accent }} />
          <Typography sx={{
            fontSize: '10.5px', letterSpacing: '0.14em', textTransform: 'uppercase',
            color: colors.accent, fontWeight: 700,
          }}>
            From current draft report
          </Typography>
        </Box>
        <Typography sx={{
          fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '15px',
          letterSpacing: '-0.005em', color: colors.textPrimary,
        }}>
          {section.title || 'Network Signals'}
        </Typography>
        {reportName && (
          <Typography sx={{ fontSize: '11px', color: colors.textSecondary, mt: 0.25 }} noWrap>
            {reportName}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: '14px 20px 18px' }}>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {bullets.map((b, i) => (
            <Box component="li" key={i} sx={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: 1, alignItems: 'flex-start' }}>
              <FiberManualRecordIcon sx={{ fontSize: 7, color: colors.accent, mt: '7px' }} />
              <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: colors.textPrimary }}>
                {b}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ── Tiered top action bar ─────────────────────────────────────────────

const TopActions = ({ onAgentChat, onDraftReport, onNextSteps, onAnalyze, analyzing, analyzeError }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {analyzeError && (
      <Typography sx={{ color: colors.critical, fontFamily: typography.fontFamily, fontSize: '11px', mr: 1 }}>
        {analyzeError}
      </Typography>
    )}

    {/* Draft Report — quiet secondary */}
    <Button
      onClick={onDraftReport}
      startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
      sx={{
        fontFamily: typography.fontFamily, fontSize: '13px', fontWeight: 500,
        color: colors.textPrimary,
        bgcolor: colors.surface, boxShadow: shadows.ambient,
        textTransform: 'none', borderRadius: '9px', px: 1.75, py: 1.25,
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: colors.surfaceElevated },
      }}
    >
      Draft Report
    </Button>

    {/* Next steps — decision tier */}
    <Button
      onClick={onNextSteps}
      startIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
      sx={{
        fontFamily: typography.fontFamily, fontSize: '13px', fontWeight: 600,
        bgcolor: colors.successContainer, color: colors.success,
        textTransform: 'none', borderRadius: '9px', px: 2, py: 1.25,
        '&:hover': { bgcolor: colors.successContainer, filter: 'brightness(0.97)' },
      }}
    >
      Next Steps
    </Button>

    {/* Agent Chat — accent, sparkled */}
    <Button
      onClick={onAgentChat}
      sx={{
        position: 'relative',
        fontFamily: typography.fontFamily, fontSize: '13px', fontWeight: 600,
        color: colors.onPrimaryContainer,
        bgcolor: colors.primaryContainer,
        textTransform: 'none', borderRadius: '9px',
        px: 1.75, py: 1.25, whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 0.875,
        transition: 'box-shadow 0.2s, filter 0.2s',
        '&:hover': {
          bgcolor: colors.primaryContainer, filter: 'brightness(0.97)',
          boxShadow: `0 0 0 3px ${colors.primaryContainer}, 0 2px 10px rgba(0,90,196,0.18)`,
        },
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 14, color: colors.primary }} />
        <AutoAwesomeIcon sx={{
          position: 'absolute', top: -4, right: -5,
          fontSize: 9, color: colors.primary,
          filter: 'drop-shadow(0 0 4px rgba(0,90,196,0.55))',
          animation: 'agentchat-sparkle 2.2s ease-in-out infinite',
          '@keyframes agentchat-sparkle': {
            '0%,100%': { opacity: 0.55, transform: 'scale(1) rotate(0deg)' },
            '50%':     { opacity: 1,    transform: 'scale(1.3) rotate(18deg)' },
          },
        }} />
      </Box>
      Agent Chat
    </Button>

    {/* Primary — gradient CTA */}
    <Button
      onClick={onAnalyze}
      disabled={analyzing}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 1,
        fontFamily: typography.fontFamily, fontWeight: 600, fontSize: '13px',
        letterSpacing: '0.01em', textTransform: 'none',
        background: gradient.primary, color: '#fff', borderRadius: '9px',
        px: 2, py: 1.25,
        boxShadow: shadows.button,
        '&:hover': { background: gradient.primary, filter: 'brightness(1.05)' },
        '&.Mui-disabled': { background: colors.surfaceElevated, color: colors.textMuted, boxShadow: 'none' },
      }}
    >
      <AutoAwesomeIcon sx={{
        fontSize: 14,
        animation: 'sparkle-pulse 2s ease-in-out infinite',
        '@keyframes sparkle-pulse': {
          '0%,100%': { opacity: 0.7, transform: 'scale(1)' },
          '50%': { opacity: 1, transform: 'scale(1.15)' },
        },
      }} />
      {analyzing ? 'Running…' : 'Analyze (agents)'}
    </Button>
  </Box>
);

// ── Case Information hero ─────────────────────────────────────────────

const Fact = ({ label, value, sub, valueColor, subColor, span = 1 }) => (
  <Box sx={{ gridColumn: `span ${span}`, minWidth: 0 }}>
    <Typography sx={{
      fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase',
      color: colors.textSecondary, fontWeight: 600, mb: 0.75,
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '16px',
      color: valueColor || colors.textPrimary, letterSpacing: '-0.01em',
    }}>
      {value ?? '—'}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: '11.5px', color: subColor || colors.textSecondary, mt: 0.3, fontWeight: subColor ? 600 : 400 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const CaseInfoHero = ({ caseData, scoredFlags = [] }) => {
  const priority = caseData.priority || caseData.risk_level;
  const relatedCases = Array.isArray(caseData.related_case_numbers) ? caseData.related_case_numbers : [];
  const existingLoans = caseData.existing_loans_total != null
    ? `${fmtMoney(caseData.existing_loans_total)}`
    : null;
  const existingSub = caseData.existing_loans_count != null
    ? `${caseData.existing_loans_count} open · current`
    : null;
  const daysToSettle = caseData.days_to_settlement;
  const settleSub = daysToSettle != null
    ? `decision needed in ${daysToSettle} day${daysToSettle === 1 ? '' : 's'}`
    : null;
  const settleSubColor = daysToSettle != null && daysToSettle <= 3 ? colors.critical : undefined;

  // ── Sanctions-case summary derived from scored red flags ──────────────
  const isSanctions = caseData.case_type === 'sanctions';
  const sanctionsSummary = useMemo(() => {
    if (!isSanctions || !Array.isArray(scoredFlags) || scoredFlags.length === 0) return null;
    const jurisdictions = new Set();
    const programs = new Set();
    const counts = { network: 0, sanctions: 0, risk_multiplier: 0 };
    let tradeDoc = null;
    let sanctionedSubject = null;
    scoredFlags.forEach((f) => {
      if (counts[f.category] != null) counts[f.category] += 1;
      const ed = f.extracted_data || {};
      const computed = ed.computed || {};
      [ed.jurisdiction, ed.sanctioned_jurisdiction, computed.sanctioned_jurisdiction, computed.actual_destination_country]
        .filter(Boolean).forEach((j) => jurisdictions.add(j));
      const progs = ed.sanctions_programs || computed.sanctions_programs;
      if (Array.isArray(progs)) progs.forEach((p) => programs.add(p));
      if (f.flag_code === 'trade_doc_destination_mismatch') tradeDoc = ed;
      if (f.flag_code === 'indirect_sanctioned_ownership') {
        sanctionedSubject = ed?.sanctioned_entity || null;
      }
    });
    return {
      jurisdictions: Array.from(jurisdictions),
      programs: Array.from(programs),
      counts,
      tradeDoc,
      sanctionedSubject,
      totalFlags: scoredFlags.length,
    };
  }, [isSanctions, scoredFlags]);

  return (
    <Box sx={{
      bgcolor: colors.surface, borderRadius: '16px',
      p: '24px 26px 22px', boxShadow: shadows.ambient,
      position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(600px 180px at 100% 0%, rgba(0,90,196,0.04), transparent 60%)',
      }} />

      {/* Header row */}
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75 }}>
        <Typography sx={{
          fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '16px',
          letterSpacing: '-0.01em',
        }}>
          Case Information
        </Typography>
        <Box sx={{ color: colors.outlineVariant }}>·</Box>
        <Typography sx={{ fontSize: '13px', color: colors.textSecondary, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
          {caseData.customer_name || caseData.customer_id}
        </Typography>
        {priority && (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            px: 1, py: 0.5, borderRadius: '6px',
            bgcolor: priority === 'Critical' ? colors.critical : priority === 'High' ? colors.warnBg : colors.primaryContainer,
            color: priority === 'Critical' ? '#fff' : priority === 'High' ? colors.warn : colors.onPrimaryContainer,
            boxShadow: priority === 'Critical' ? '0 2px 8px rgba(187,27,33,0.25)' : 'none',
          }}>
            {priority}
          </Box>
        )}
      </Box>

      {/* AI summary */}
      {caseData.ai_summary && (
        <Box sx={{
          display: 'grid', gridTemplateColumns: '18px 1fr', gap: 1.5, alignItems: 'flex-start',
          bgcolor: colors.canvas, borderRadius: '12px', p: '14px 16px', mb: 2.5, position: 'relative',
        }}>
          <AutoAwesomeIcon sx={{
            color: colors.primary, fontSize: 16, mt: 0.25,
            animation: 'sparkle-pulse 2s ease-in-out infinite',
            '@keyframes sparkle-pulse': { '0%,100%': { opacity: 0.7, transform: 'scale(1)' }, '50%': { opacity: 1, transform: 'scale(1.15)' } },
          }} />
          <Box>
            <Typography sx={{
              fontSize: '10.5px', letterSpacing: '0.14em', textTransform: 'uppercase',
              color: colors.primary, fontWeight: 700, mb: 0.5,
            }}>
              AI Summary · Why it got flagged
            </Typography>
            <Typography sx={{ fontSize: '13px', color: colors.textPrimary, lineHeight: 1.55, whiteSpace: 'pre-line' }}>
              {caseData.ai_summary}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Fact grid — sanctions-case variant */}
      {isSanctions && sanctionsSummary ? (
        <Box sx={{
          position: 'relative',
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px 28px',
        }}>
          <Fact
            label="Primary subject"
            value={sanctionsSummary.sanctionedSubject?.name || caseData.customer_name || caseData.customer_id || '—'}
            sub={sanctionsSummary.sanctionedSubject?.list
              ? `${sanctionsSummary.sanctionedSubject.list} match`
              : (caseData.customer_id || null)}
            subColor={sanctionsSummary.sanctionedSubject?.list ? colors.critical : undefined}
          />
          <Fact
            label="Jurisdictions touched"
            value={sanctionsSummary.jurisdictions.length > 0 ? sanctionsSummary.jurisdictions.join(', ') : '—'}
            sub={sanctionsSummary.jurisdictions.length > 0 ? 'sanctioned / high-risk' : null}
            subColor={sanctionsSummary.jurisdictions.length > 0 ? colors.critical : undefined}
          />
          <Fact
            label="Scored flags"
            value={`${sanctionsSummary.totalFlags}`}
            sub={`${sanctionsSummary.counts.network} network · ${sanctionsSummary.counts.sanctions} sanctions · ${sanctionsSummary.counts.risk_multiplier} multiplier`}
          />
        </Box>
      ) : (
      <Box sx={{
        position: 'relative',
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px 28px',
      }}>
        <Fact label="Loan amount" value={fmtMoney(caseData.facility_amount) || '—'} sub={caseData.facility_type} />
        <Fact label="Applied" value={fmtDate(caseData.applied_date) || '—'} sub={caseData.application_channel || 'Intake'} />
        <Fact
          label="Deadline"
          value={fmtDate(caseData.settlement_date) || '—'}
          sub={settleSub} subColor={settleSubColor}
        />
        <Fact label="Industry" value={caseData.industry_segment || '—'} sub={caseData.industry_category} />

        <Fact label="Existing loans" value={existingLoans || '—'} sub={existingSub} />
        <Fact
          label="Registered address"
          value={caseData.business_address || '—'}
          sub={caseData.premises_note}
          span={2}
        />
        {relatedCases.length > 0 && (
          <Box>
            <Typography sx={{
              fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase',
              color: colors.textSecondary, fontWeight: 600, mb: 0.75,
            }}>
              Related cases
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {relatedCases.map((n) => (
                <Box
                  key={n}
                  component="a"
                  href={`/cases/${n}`}
                  sx={{
                    fontFamily: typography.fontFamilyMono, fontSize: '12px', fontWeight: 600,
                    color: colors.primary, textDecoration: 'none',
                    borderBottom: '1px dashed rgba(0,90,196,0.35)',
                  }}
                >
                  REF-{n}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      )}
    </Box>
  );
};

// ── Score dial card ───────────────────────────────────────────────────

const ScoreDial = ({ score }) => {
  const safe = Math.max(0, Math.min(100, score ?? 0));
  const R = 66;
  const C = 2 * Math.PI * R; // ~414.7
  const dashOffset = C * (1 - safe / 100);
  const dialColor = safe >= 70 ? colors.critical : safe >= 40 ? colors.warn : colors.success;

  return (
    <Box sx={{
      bgcolor: colors.surface, borderRadius: '16px', p: '22px 22px 20px',
      boxShadow: shadows.ambient, position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', gap: 1.75,
    }}>
      <Box sx={{
        position: 'absolute', inset: '-80px -80px auto auto',
        width: 220, height: 220, pointerEvents: 'none',
        background: `radial-gradient(circle, ${safe >= 70 ? 'rgba(187,27,33,0.10)' : safe >= 40 ? 'rgba(139,94,14,0.10)' : 'rgba(47,107,90,0.10)'}, transparent 70%)`,
      }} />

      <Box sx={{
        fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase',
        color: colors.textSecondary, fontWeight: 600, position: 'relative',
      }}>
        <Box component="span">Case Score</Box>
      </Box>

      <Box sx={{ position: 'relative', width: 160, height: 160, alignSelf: 'center' }}>
        <Box component="svg" viewBox="0 0 160 160" sx={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
          <defs>
            <linearGradient id="scoreGrad" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%"  stopColor={dialColor} />
              <stop offset="100%" stopColor={safe >= 70 ? '#e04b4b' : safe >= 40 ? '#c88b1c' : '#5ba48c'} />
            </linearGradient>
          </defs>
          <circle cx="80" cy="80" r={R} fill="none" stroke={colors.surfaceLow} strokeWidth="12" />
          <circle
            cx="80" cy="80" r={R} fill="none" stroke="url(#scoreGrad)" strokeWidth="12"
            strokeLinecap="round" strokeDasharray={C} strokeDashoffset={dashOffset}
          />
        </Box>
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{
            fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '48px',
            letterSpacing: '-0.03em', lineHeight: 1, color: dialColor,
          }}>
            {safe}
          </Typography>
          <Mono sx={{ fontSize: '11px', color: colors.textSecondary, mt: 0.5, letterSpacing: '0.06em' }}>
            / 100
          </Mono>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: colors.textSecondary, position: 'relative' }}>
        <Box component="span">Low</Box>
        <Box sx={{
          flex: 1, height: 4, mx: 1.25, borderRadius: '2px', position: 'relative',
          background: 'linear-gradient(90deg, #2f6b5a 0%, #c88b1c 55%, #bb1b21 100%)',
        }}>
          <Box sx={{
            position: 'absolute', top: -3, left: `${safe}%`,
            width: 10, height: 10, borderRadius: '50%',
            bgcolor: colors.surface, border: `2px solid ${dialColor}`,
            boxShadow: `0 2px 6px ${dialColor}55`, transform: 'translateX(-50%)',
          }} />
        </Box>
        <Box component="span" sx={{ color: colors.critical, fontWeight: 700 }}>High</Box>
      </Box>
    </Box>
  );
};

// ── Side deck (Timeline / Verify / Intel / Fund Flow) ─────────────────

const SideDeck = ({ tabs, activeIdx, onChange }) => {
  const stackRef = useRef(null);

  // keyboard arrow nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  onChange((activeIdx - 1 + tabs.length) % tabs.length);
      if (e.key === 'ArrowRight') onChange((activeIdx + 1) % tabs.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIdx, tabs.length, onChange]);

  // pointer swipe
  const handlePointerDown = (e) => { stackRef.current._startX = e.clientX; };
  const handlePointerUp = (e) => {
    const sx = stackRef.current._startX;
    if (sx == null) return;
    const dx = e.clientX - sx;
    if (Math.abs(dx) > 40) {
      if (dx < 0) onChange((activeIdx + 1) % tabs.length);
      else        onChange((activeIdx - 1 + tabs.length) % tabs.length);
    }
    stackRef.current._startX = null;
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1.5,
      alignSelf: 'start', position: 'sticky', top: 20,
    }}>
      {/* Tab strip */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
        bgcolor: colors.surface, borderRadius: '10px', p: 0.5, gap: 0.25,
        boxShadow: shadows.ambient,
      }}>
        {tabs.map(({ key, label, Icon }, i) => {
          const active = i === activeIdx;
          return (
            <Box
              key={key}
              onClick={() => onChange(i)}
              sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                p: '9px 6px', fontSize: '11.5px', fontWeight: active ? 600 : 500,
                color: active ? colors.onPrimaryContainer : colors.textSecondary,
                bgcolor: active ? colors.primaryContainer : 'transparent',
                borderRadius: '7px', cursor: 'pointer',
                fontFamily: typography.fontFamily,
                transition: 'background 0.15s, color 0.15s',
                '&:hover': active ? {} : { color: colors.textPrimary },
              }}
            >
              <Icon sx={{ fontSize: 13 }} />
              {label}
            </Box>
          );
        })}
      </Box>

      {/* Deck stack */}
      <Box
        ref={stackRef}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        sx={{ position: 'relative', height: 460, perspective: '1200px' }}
      >
        {tabs.map(({ key, content }, i) => {
          const isActive = i === activeIdx;
          const isPeek = i === (activeIdx + 1) % tabs.length;
          return (
            <Box
              key={key}
              sx={{
                position: 'absolute', inset: 0,
                bgcolor: colors.surface, borderRadius: '16px',
                p: '20px 22px', boxShadow: shadows.ambient,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease',
                transformOrigin: 'center',
                visibility: isActive ? 'visible' : (isPeek ? 'visible' : 'hidden'),
                pointerEvents: isActive ? 'auto' : 'none',
                opacity: isActive ? 1 : (isPeek ? 0.35 : 0),
                transform: isActive ? 'translateY(0) scale(1)'
                         : isPeek ? 'translateY(10px) scale(0.97)'
                         : 'translateY(0) scale(1)',
                zIndex: isActive ? 2 : (isPeek ? 1 : 0),
              }}
            >
              {content}
            </Box>
          );
        })}
      </Box>

      {/* Deck nav */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 0.5, pt: 0.5,
      }}>
        <IconButton
          onClick={() => onChange((activeIdx - 1 + tabs.length) % tabs.length)}
          sx={{
            width: 30, height: 30, bgcolor: colors.surface, color: colors.textPrimary,
            boxShadow: shadows.ambient,
            '&:hover': { bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {tabs.map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i === activeIdx ? 18 : 6, height: 6,
                bgcolor: i === activeIdx ? colors.primary : colors.surfaceContainer,
                borderRadius: i === activeIdx ? '3px' : '50%',
                transition: 'all 0.2s',
              }}
            />
          ))}
        </Box>
        <IconButton
          onClick={() => onChange((activeIdx + 1) % tabs.length)}
          sx={{
            width: 30, height: 30, bgcolor: colors.surface, color: colors.textPrimary,
            boxShadow: shadows.ambient,
            '&:hover': { bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

const DeckHeader = ({ title, meta }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.75, flexShrink: 0 }}>
    <Typography sx={{ fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '15px' }}>
      {title}
    </Typography>
    {meta && <Mono sx={{ fontSize: '10.5px', color: colors.textSecondary, letterSpacing: '0.08em' }}>{meta}</Mono>}
  </Box>
);

// ── Queue pager ───────────────────────────────────────────────────────

const QueuePager = ({ prevCase, nextCase, position, total, onNavigate }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '10px 4px', fontSize: '12px', color: colors.textSecondary }}>
    <Button
      disabled={!prevCase}
      onClick={() => prevCase && onNavigate(prevCase.case_number)}
      startIcon={<ChevronLeftIcon sx={{ fontSize: 12 }} />}
      sx={{
        bgcolor: colors.surface, boxShadow: shadows.ambient, borderRadius: '8px',
        fontFamily: typography.fontFamily, fontSize: '12px', fontWeight: 600,
        color: colors.textPrimary, textTransform: 'none', px: 1.75, py: 1,
        '&:hover': { bgcolor: colors.surfaceElevated },
        '&.Mui-disabled': { opacity: 0.4 },
      }}
    >
      Previous case
    </Button>
    <Mono>
      Case {position} of {total} · Queue
    </Mono>
    <Button
      disabled={!nextCase}
      onClick={() => nextCase && onNavigate(nextCase.case_number)}
      endIcon={<ChevronRightIcon sx={{ fontSize: 12 }} />}
      sx={{
        bgcolor: colors.surface, boxShadow: shadows.ambient, borderRadius: '8px',
        fontFamily: typography.fontFamily, fontSize: '12px', fontWeight: 600,
        color: colors.textPrimary, textTransform: 'none', px: 1.75, py: 1,
        '&:hover': { bgcolor: colors.surfaceElevated },
        '&.Mui-disabled': { opacity: 0.4 },
      }}
    >
      Next case
    </Button>
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────

const CaseView = () => {
  const { caseNumber } = useParams();
  const navigate = useNavigate();

  // Server state
  const { data: caseData, isLoading: caseLoading, error: caseError } = useCase(caseNumber);
  const { data: graphData, isLoading: graphLoading } = useCaseGraph(caseNumber);
  const { data: redFlagsData, isLoading: rfLoading } = useCaseRedFlags(caseNumber);
  const { data: verificationsData, isLoading: verificationsLoading } = useCaseVerifications(caseNumber);
  const { data: scoredRedFlagsData } = useScoredRedFlags(caseNumber);
  const hasScoredFlags = (scoredRedFlagsData?.flags?.length ?? 0) > 0;
  const { data: osintData, isLoading: osintLoading } = useOsint(caseNumber);
  const { data: depositTraceData, isLoading: depositTraceLoading } = useDepositTrace(caseNumber);
  const { data: networkOverride } = useCaseNetwork(caseNumber);
  const { data: draftReport } = useDraftReport(caseNumber);
  const { data: casesList } = useCases({ limit: 100 });

  const networkSignalsSection = useMemo(() => {
    const sections = draftReport?.sections ?? [];
    return sections.find((s) => s.key === 'network_signals')
      || sections.find((s) => /network\s*signal/i.test(s.title || ''))
      || null;
  }, [draftReport]);

  // Analysis
  const agenticAnalysis = useAgenticAnalysis(caseNumber);

  // Local UI
  const [graphMaximized, setGraphMaximized] = useState(false);
  const [sideTab, setSideTab] = useState(0);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [reportStructured, setReportStructured] = useState(null);
  const [openAgentModal, setOpenAgentModal] = useState(false);
  const [openAgentChat, setOpenAgentChat] = useState(false);
  const [openReportEditor, setOpenReportEditor] = useState(false);
  const [openDecision, setOpenDecision] = useState(false);
  const [isAgenticRun, setIsAgenticRun] = useState(false);
  // Agent keys depend on case_type:
  //   application_fraud  → fraud / network / risk_multiplier
  //   sanctions (scored) → network / sanctions / risk_multiplier
  //   sanctions (legacy) → rf01 / rf02 / rf03  (pre-scored-flags cases only)
  // We drive the modal + ReportDialog from whatever the backend returns.
  const [activeCaseType, setActiveCaseType] = useState('sanctions');
  const [agentProgress, setAgentProgress] = useState({ master: 'idle' });
  const [agentSummaries, setAgentSummaries] = useState({ master: '' });

  const autoTransitionRef = useRef(null);
  const openReportTimerRef = useRef(null);
  useEffect(() => {
    if (agentProgress.master === 'done' && openAgentModal) {
      autoTransitionRef.current = setTimeout(() => {
        setOpenAgentModal(false);
        // Wait for the AgentAnalysisModal's exit transition (MUI Dialog fade is
        // ~225ms) before mounting the report dialog. Overlapping backdrops
        // during the crossfade cause a brief flash otherwise.
        openReportTimerRef.current = setTimeout(() => setOpenReportDialog(true), 260);
      }, 1800);
    }
    // Only clear the outer timer on re-run. The inner timer is scheduled from
    // inside the outer timer's callback and must be allowed to fire even after
    // `openAgentModal` flips to false (which re-runs this effect).
    return () => { clearTimeout(autoTransitionRef.current); };
  }, [agentProgress.master, openAgentModal]);

  // Unmount-only cleanup for the deferred open-report timer.
  useEffect(() => () => clearTimeout(openReportTimerRef.current), []);

  useEffect(() => {
    if (!graphMaximized) return;
    const onKey = (e) => { if (e.key === 'Escape') setGraphMaximized(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [graphMaximized]);
  useEffect(() => {
    const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 80);
    return () => clearTimeout(t);
  }, [graphMaximized]);

  // ── Derived graph data ────────────────────────────────────────────
  const sourceElements = networkOverride?.elements ?? graphData?.elements ?? { nodes: [], edges: [] };
  const nodes = useMemo(() => (sourceElements.nodes ?? []).map((n) => ({
    ...n,
    data: { ...n.data, riskRating: n.data.risk_rating ?? n.data.riskRating, type: n.data.type ?? n.data.entity_type },
  })), [sourceElements.nodes]);
  const edges = useMemo(() => (sourceElements.edges ?? []).map((e) => ({
    ...e,
    data: {
      ...e.data,
      type: e.data.edge_type
        ? e.data.edge_type.charAt(0).toUpperCase() + e.data.edge_type.slice(1)
        : e.data.relationship || e.data.label || 'Unknown',
    },
  })), [sourceElements.edges]);
  const graphViewData = useMemo(() => ({ nodes, edges }), [nodes, edges]);
  const primeSuspectId = networkOverride?.prime_suspect_id ?? graphData?.prime_suspect_id ?? caseData?.customer_id ?? null;

  // Red flags fallback
  const rf01Details = redFlagsData?.rf01_details ?? [];
  const rf02Details = redFlagsData?.rf02_details ?? [];
  const rf03Details = redFlagsData?.rf03_details ?? [];
  const rf04Details = redFlagsData?.rf04_details ?? [];
  const redFlagsTabs = caseData?.case_type === 'money mule'
    ? { 'Redflag 01': rf01Details, 'Redflag 02': rf02Details, 'Redflag 04': rf04Details }
    : { 'Redflag 01': rf01Details, 'Redflag 02': rf02Details, 'Redflag 03': rf03Details };

  // Queue nav
  const queueCases = casesList?.cases ?? [];
  const myIndex = queueCases.findIndex((c) => String(c.case_number) === String(caseNumber));
  const prevCase = myIndex > 0 ? queueCases[myIndex - 1] : null;
  const nextCase = myIndex >= 0 && myIndex < queueCases.length - 1 ? queueCases[myIndex + 1] : null;

  // ── Handlers ──────────────────────────────────────────────────────
  const handleViewVerdict = () => {
    setOpenAgentModal(false);
    // Sequence the modal handoff so the AgentAnalysisModal finishes its exit
    // transition before the report dialog mounts (avoids a backdrop flicker).
    openReportTimerRef.current = setTimeout(() => setOpenReportDialog(true), 260);
  };

  const handleGenerateAgenticSTR = async () => {
    // Seed the modal before we know the backend's chosen case_type so that the
    // user sees progress immediately. If caseData.case_type is already loaded
    // we can show the right roster on first paint; otherwise the default
    // ('sanctions') is overridden when the response arrives.
    const seedCaseType = caseData?.case_type || 'sanctions';
    const seedKeys = seedCaseType === 'application_fraud'
      ? ['fraud', 'network', 'risk_multiplier']
      : ['network', 'sanctions', 'risk_multiplier'];

    const loadingProgress = { master: 'idle' };
    const emptySummaries = { master: '' };
    seedKeys.forEach((k) => { loadingProgress[k] = 'loading'; emptySummaries[k] = ''; });

    setActiveCaseType(seedCaseType);
    setIsAgenticRun(true);
    setOpenAgentModal(true);
    setReportStructured(null);
    setAgentSummaries(emptySummaries);
    setAgentProgress(loadingProgress);

    try {
      const result = await agenticAnalysis.mutateAsync();
      const toStatus = (step) => (step?.status === 'completed' ? 'done' : step?.status === 'failed' ? 'error' : 'done');
      const extractSummary = (text) => {
        if (!text) return '';
        const firstLine = text.split('\n').find((l) => l.trim().length > 20) || '';
        const clean = firstLine.replace(/^#+\s*/, '').trim();
        return clean.length > 140 ? clean.slice(0, 140) + '…' : clean;
      };

      const resolvedCaseType = result.case_type || seedCaseType;
      const agents = result.agents || {
        // Back-compat: older sanctions responses carried rf01/rf02/rf03 as top-level fields.
        rf01: result.rf01, rf02: result.rf02, rf03: result.rf03,
      };
      const agentKeys = Object.keys(agents);

      setActiveCaseType(resolvedCaseType);

      const nextProgress = { master: 'loading' };
      const nextSummaries = { master: '' };
      agentKeys.forEach((k) => {
        nextProgress[k] = toStatus(agents[k]);
        nextSummaries[k] = extractSummary(agents[k]?.output);
      });
      setAgentProgress(nextProgress);
      setAgentSummaries(nextSummaries);

      await new Promise((r) => setTimeout(r, 600));
      setAgentProgress((prev) => ({ ...prev, master: toStatus(result.master) }));
      setAgentSummaries((prev) => ({
        ...prev,
        // Sanctions master emits `assessment`; fraud master emits `summary`.
        master: result.structured_report?.assessment
          || result.structured_report?.summary
          || extractSummary(result.master?.output),
      }));
      setReportStructured(result.structured_report ?? null);
      setReportContent(result.structured_report?.fullReport || result.master?.output || '');
    } catch (err) {
      // Mark every seeded key + master as errored.
      const errState = { master: 'error' };
      seedKeys.forEach((k) => { errState[k] = 'error'; });
      setAgentProgress(errState);
      // eslint-disable-next-line no-console
      console.error('Agentic analysis failed:', err);
    } finally {
      setIsAgenticRun(false);
    }
  };

  const handleSaveReport = () => {
    const savedReports = JSON.parse(localStorage.getItem('reports') || '[]');
    const reportIndex = savedReports.filter((r) => r.case_id === caseNumber).length + 1;
    const newReport = {
      id: `${caseNumber}_${Date.now()}`, case_id: caseNumber,
      name: `report_${reportIndex}`, created_at: new Date().toISOString(), content: reportContent,
    };
    localStorage.setItem('reports', JSON.stringify([...savedReports, newReport]));
    setOpenReportDialog(false);
  };

  // ── Loading / error ───────────────────────────────────────────────
  if (caseLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', bgcolor: colors.bg }}>
        <CircularProgress sx={{ color: colors.accent }} />
      </Box>
    );
  }
  if (caseError || !caseData) {
    return (
      <Box sx={{ p: 4, bgcolor: colors.bg, minHeight: '100vh' }}>
        <Alert severity="error" sx={{ fontFamily: typography.fontFamily }}>
          {caseError?.message || 'Case not found'}
        </Alert>
      </Box>
    );
  }

  const analyzing = agenticAnalysis.isPending || isAgenticRun;
  const analysisError = agenticAnalysis.error;
  const score = caseData.composite_score ?? Math.round((caseData.ai_rating ?? 0) * 100);
  const entityCount = nodes.length;
  const edgeCount = edges.length;

  // Side deck tabs
  const hasOsint = osintLoading || (osintData?.sources?.length ?? 0) > 0;
  const hasDepositTrace = depositTraceLoading || (depositTraceData?.hops?.length ?? 0) > 0;
  const deckTabs = [
    caseData.alert_timeline && {
      key: 'timeline', label: 'Timeline', Icon: TimelineOutlinedIcon,
      content: (
        <>
          <DeckHeader title="Alert Timeline" meta="LIVE" />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <AlertTimeline events={caseData.alert_timeline} settlementDate={caseData.settlement_date} />
          </Box>
        </>
      ),
    },
    {
      key: 'verification', label: 'Verify', Icon: FactCheckOutlinedIcon,
      content: (
        <>
          <DeckHeader title="Verification" meta={`${verificationsData?.verifications?.length ?? 0} CHECKS`} />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <IndependentVerificationPanel
              verifications={verificationsData?.verifications}
              summary={verificationsData?.summary}
              isLoading={verificationsLoading}
            />
          </Box>
        </>
      ),
    },
    hasOsint && {
      key: 'osint', label: 'Intel', Icon: LanguageOutlinedIcon,
      content: (
        <>
          <DeckHeader title="Intelligence" meta={`${osintData?.sources?.length ?? 0} SIGNALS`} />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <OsintPanel sources={osintData?.sources} isLoading={osintLoading} />
          </Box>
        </>
      ),
    },
    hasDepositTrace && {
      key: 'deposit', label: 'Fund Flow', Icon: TrendingUpIcon,
      content: (
        <>
          <DeckHeader title="Fund Flow" meta="LAST 30D" />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <DepositTraceCard trace={depositTraceData} isLoading={depositTraceLoading} />
          </Box>
        </>
      ),
    },
  ].filter(Boolean);

  const safeTab = Math.min(sideTab, Math.max(0, deckTabs.length - 1));

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Box sx={{
      p: '24px 32px 40px',
      display: 'flex', flexDirection: 'column', gap: 2.5,
      minWidth: 0, minHeight: '100vh', bgcolor: colors.bg,
    }}>

      {/* Title + actions */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'flex-start', gap: 3 }}>
        <Box>
          <Typography sx={{ fontSize: '12px', color: colors.textSecondary, mb: 0.75 }}>
            <Box component="a" onClick={() => navigate('/cases')} sx={{ color: colors.primary, cursor: 'pointer', textDecoration: 'none' }}>
              Cases
            </Box>
            {' / '}
            <Box component="span">REF-{caseData.case_number}</Box>
          </Typography>
          <Typography sx={{
            fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '28px',
            letterSpacing: '-0.02em', color: colors.textPrimary, lineHeight: 1.2,
          }}>
            <Box component="span" sx={{ color: colors.textSecondary, fontWeight: 700 }}>REF-{caseData.case_number}</Box>
            {' — '}{caseData.customer_name || caseData.customer_id}
          </Typography>
          <Box sx={{
            mt: 0.75, fontSize: '12.5px', color: colors.textSecondary,
            display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap',
          }}>
            {caseData.customer_abn && <Mono sx={{ color: colors.textSecondary }}>ABN · {caseData.customer_abn}</Mono>}
            {caseData.customer_abn && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.outlineVariant }} />}
            {caseData.applied_date && <Box component="span">Opened {fmtDate(caseData.applied_date)}</Box>}
            {caseData.assigned_to && (<>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.outlineVariant }} />
              <Box component="span">Assigned to <Box component="strong" sx={{ color: colors.textPrimary, fontWeight: 600 }}>{caseData.assigned_to}</Box></Box>
            </>)}
            {caseData.risk_level && (<>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.outlineVariant }} />
              <Box component="span" sx={{
                color: caseData.risk_level === 'Critical' ? colors.critical : caseData.risk_level === 'High' ? colors.warn : colors.primary,
                fontWeight: 600,
              }}>
                ● {caseData.risk_level}
              </Box>
            </>)}
          </Box>
        </Box>

        <TopActions
          onAgentChat={() => setOpenAgentChat(true)}
          onDraftReport={() => setOpenReportEditor(true)}
          onNextSteps={() => setOpenDecision(true)}
          onAnalyze={handleGenerateAgenticSTR}
          analyzing={analyzing}
          analyzeError={analysisError?.message}
        />
      </Box>

      {/* Hero: info + score */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 2.5 }}>
        <CaseInfoHero caseData={caseData} scoredFlags={scoredRedFlagsData?.flags || []} />
        <ScoreDial score={score} />
      </Box>

      {/* Graph + side deck */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 2.5 }}>
        {/* Graph panel */}
        <Box sx={
          graphMaximized
            ? {
                bgcolor: colors.surface, borderRadius: '16px', boxShadow: shadows.elevated,
                position: 'fixed',
                top: 16, left: 16, bottom: 16,
                right: networkSignalsSection ? 376 : 16,
                zIndex: 1300,
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
              }
            : {
                bgcolor: colors.surface, borderRadius: '16px', boxShadow: shadows.ambient,
                overflow: 'hidden',
              }
        }>
          <Box sx={{
            p: '20px 24px 10px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Box>
              <Typography sx={{ fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '16px', letterSpacing: '-0.005em' }}>
                Risk Network Graph
              </Typography>
              <Typography sx={{ fontSize: '12px', color: colors.textSecondary, mt: 0.25 }}>
                Entities, counter-parties & cluster links for this case
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Mono sx={{ fontSize: '10.5px', color: colors.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {entityCount} Entities · {edgeCount} Edges
              </Mono>
              <IconButton
                size="small"
                onClick={() => setGraphMaximized(v => !v)}
                sx={{ color: colors.textSecondary, '&:hover': { color: colors.accent, bgcolor: colors.accentDim } }}
              >
                {graphMaximized ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{
            position: 'relative', m: '8px 16px 16px', height: graphMaximized ? 'calc(100vh - 140px)' : 520,
            borderRadius: '12px',
            background: 'radial-gradient(circle at 50% 50%, #f8f9fb 0%, #eef2f6 100%)',
            overflow: 'hidden',
          }}>
            {/* Grid mask */}
            <Box sx={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              backgroundImage:
                'linear-gradient(rgba(0,90,196,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,90,196,0.05) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              maskImage: 'radial-gradient(circle at 50% 50%, black 45%, transparent 85%)',
              WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 45%, transparent 85%)',
            }} />
            {graphLoading
              ? (
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                  <CircularProgress size={28} sx={{ color: colors.accent }} />
                </Box>
              )
              : (
                <GraphView data={graphViewData} onNodeClick={() => {}} primeSuspectId={primeSuspectId} caseType={caseData?.case_type} />
              )
            }
          </Box>
        </Box>
        {graphMaximized && (
          <Box
            onClick={() => setGraphMaximized(false)}
            sx={{
              position: 'fixed', inset: 0, zIndex: 1299,
              bgcolor: 'rgba(43, 52, 56, 0.35)', backdropFilter: 'blur(2px)',
            }}
          />
        )}
        {graphMaximized && (
          <NetworkSignalsCard
            section={networkSignalsSection}
            reportName={draftReport?.report_name}
          />
        )}

        {/* Side deck */}
        {deckTabs.length > 0 && (
          <SideDeck
            tabs={deckTabs}
            activeIdx={safeTab}
            onChange={setSideTab}
          />
        )}
      </Box>

      {/* Red flags */}
      {hasScoredFlags ? (
        <ScoredRedFlagsList caseNumber={caseNumber} />
      ) : (
        <Box sx={{ bgcolor: colors.surface, borderRadius: '16px', p: 2.5, boxShadow: shadows.ambient }}>
          {rfLoading
            ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: colors.accent }} /></Box>
            : <RedFlagsCard tabsData={redFlagsTabs} />
          }
        </Box>
      )}

      {/* Queue pager */}
      {queueCases.length > 0 && myIndex >= 0 && (
        <QueuePager
          prevCase={prevCase}
          nextCase={nextCase}
          position={myIndex + 1}
          total={queueCases.length}
          onNavigate={(cn) => navigate(`/cases/${cn}`)}
        />
      )}

      {/* Panels / dialogs */}
      <AgentChatPanel open={openAgentChat} onClose={() => setOpenAgentChat(false)} caseNumber={caseNumber} />
      <ReportEditor open={openReportEditor} onClose={() => setOpenReportEditor(false)} caseNumber={caseNumber} />
      <DecisionPanel open={openDecision} onClose={() => setOpenDecision(false)} caseNumber={caseNumber} />
      <AgentAnalysisModal
        open={openAgentModal} caseNumber={caseNumber}
        caseType={activeCaseType}
        agentProgress={agentProgress} agentSummaries={agentSummaries}
        onViewVerdict={handleViewVerdict} caseData={caseData}
        redFlagCount={scoredRedFlagsData?.flags?.length
          ?? (rf01Details.length + rf02Details.length + rf03Details.length + rf04Details.length)}
      />
      <ReportDialog
        open={openReportDialog} onClose={() => setOpenReportDialog(false)}
        onSave={handleSaveReport}
        caseType={activeCaseType}
        reportContent={reportContent} reportStructured={reportStructured}
        reportError={analysisError?.message ?? null}
        caseNumber={caseNumber} caseData={caseData} graphNodes={nodes} graphEdges={edges} primeSuspectId={primeSuspectId}
      />
    </Box>
  );
};

export default CaseView;
